import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Package, Loader, AlertCircle, HelpCircle, Gem, ChevronDown, ChevronUp, Clock, Flame } from 'lucide-react'
import { motion } from 'motion/react'
import useCasesStore from '../store/useCasesStore'
import useAppStore from '../store/useAppStore'
import HelpModal from '../components/HelpModal'
import GameTabs from '../components/GameTabs'
import useAudioManager from '../hooks/useAudioManager'
import { formatMinutesLeft } from '../utils/formatters'
import TeamCapturePreview from '../features/team/components/TeamCapturePreview'

const MAX_VISIBLE_CATEGORIES = 6

const getCaseCategories = (caseItem) => {
  if (!Array.isArray(caseItem.category)) {
    return []
  }

  return caseItem.category.filter(
    (category) => typeof category === 'string' && category.trim() !== ''
  )
}

const sortCategories = (categories, categoryOrder = []) => {
  const uniqueCategories = Array.from(new Set(categories))

  if (!Array.isArray(categoryOrder) || categoryOrder.length === 0) {
    const hasLimited = uniqueCategories.includes('limited')
    const sortedCategories = uniqueCategories
      .filter((category) => category !== 'limited')
      .sort((a, b) => a.localeCompare(b, 'ru'))

    return hasLimited ? ['limited', ...sortedCategories] : sortedCategories
  }

  // categoryOrder — массив объектов {slug, name}
  const categoryOrderMap = new Map(
    categoryOrder.map((cat, index) => [cat.slug, index])
  )

  const orderedCategories = uniqueCategories
    .filter((category) => categoryOrderMap.has(category))
    .sort((a, b) => categoryOrderMap.get(a) - categoryOrderMap.get(b))

  const unorderedCategories = uniqueCategories
    .filter((category) => !categoryOrderMap.has(category))
    .sort((a, b) => a.localeCompare(b, 'ru'))

  return [...orderedCategories, ...unorderedCategories]
}

const Cases = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { cases, categoryOrder, games, loadCases, isLoading, error } = useCasesStore()
  const { balance, user, language, isGuest } = useAppStore()
  const { playSound } = useAudioManager({
    hover: 'hover',
    pick: 'pick',
    select: 'select'
  })

  // Приоритет: query-параметр ?category= > localStorage > 'All'
  const [activeCategory, setActiveCategory] = useState(() => {
    const urlCategory = searchParams.get('category')
    if (urlCategory) return urlCategory
    return localStorage.getItem('selectedCaseCategory') || 'All'
  })
  const [showAllCategories, setShowAllCategories] = useState(false)
  const [shiningCaseId, setShiningCaseId] = useState(null)

  // Активная игра: по умолчанию cs2, иначе первая из games[]
  const [activeGame, setActiveGame] = useState(() => {
    return localStorage.getItem('selectedGame') || 'cs2'
  })

  // Сохраняем выбранную игру в localStorage
  useEffect(() => {
    localStorage.setItem('selectedGame', activeGame)
  }, [activeGame])

  // Корректируем активную игру, если её нет в games[]
  useEffect(() => {
    if (games.length > 0 && !games.some(g => g.slug === activeGame)) {
      setActiveGame(games[0].slug)
    }
  }, [games, activeGame])

  // Восстанавливаем позицию скролла при возврате на страницу
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('casesScrollPosition')
    if (savedScrollPosition && cases.length > 0) {
      // Небольшая задержка для корректного восстановления после рендера
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition, 10))
      }, 0)
    }
  }, [cases.length])

  // Сохраняем позицию скролла при уходе со страницы
  useEffect(() => {
    const handleScroll = () => {
      sessionStorage.setItem('casesScrollPosition', window.scrollY.toString())
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    // Stale-while-revalidate: всегда перезапрашиваем список при входе в раздел
    loadCases()
  }, [loadCases, language, isGuest])

  // Сохраняем выбранную категорию в localStorage и синхронизируем URL
  useEffect(() => {
    localStorage.setItem('selectedCaseCategory', activeCategory)
    if (activeCategory === 'All') {
      setSearchParams({}, { replace: true })
    } else {
      setSearchParams({ category: activeCategory }, { replace: true })
    }
  }, [activeCategory, setSearchParams])

  const handleCaseClick = (caseId) => {
    playSound('select')
    navigate(`/cases/${caseId}`)
  }

  const handleCaseHover = () => {
    playSound('hover')
  }

  const categorizedCases = useMemo(() => {
    // Фильтруем по выбранной игре, затем по категориям
    const filteredCases = cases
      .filter((caseItem) => caseItem.name !== 'Welcome Case')
      .filter((caseItem) => caseItem.game === activeGame)
      .map((caseItem) => ({
        ...caseItem,
        categories: getCaseCategories(caseItem)
      }))
      .filter((caseItem) => caseItem.categories.length > 0)

    const casesByCategory = new Map()

    filteredCases.forEach((caseItem) => {
      caseItem.categories.forEach((category) => {
        if (!casesByCategory.has(category)) {
          casesByCategory.set(category, [])
        }

        casesByCategory.get(category).push(caseItem)
      })
    })

    return sortCategories(Array.from(casesByCategory.keys()), categoryOrder).map((category) => ({
      category,
      cases: [...casesByCategory.get(category)].sort(
        (a, b) => Number(a.price) - Number(b.price)
      )
    }))
  }, [cases, categoryOrder, activeGame])

  const availableCategories = useMemo(() => {
    return ['All', ...categorizedCases.map(({ category }) => category)]
  }, [categorizedCases])

  const hasHiddenCategories = availableCategories.length > MAX_VISIBLE_CATEGORIES

  const visibleCategoryTabs = useMemo(() => {
    if (showAllCategories || !hasHiddenCategories) {
      return availableCategories
    }

    const defaultVisibleCategories = availableCategories.slice(0, MAX_VISIBLE_CATEGORIES)

    if (activeCategory && availableCategories.includes(activeCategory) && !defaultVisibleCategories.includes(activeCategory)) {
      return [...defaultVisibleCategories, activeCategory]
    }

    return defaultVisibleCategories
  }, [activeCategory, availableCategories, hasHiddenCategories, showAllCategories])

  const visibleSections = useMemo(() => {
    if (activeCategory === 'All') {
      return categorizedCases
    }

    return categorizedCases.filter(({ category }) => category === activeCategory)
  }, [categorizedCases, activeCategory])

  useEffect(() => {
    // Сбрасываем только если cases загружены и категории действительно нет в списке
    if (!isLoading && cases.length > 0 && availableCategories.length > 1 && !availableCategories.includes(activeCategory)) {
      setActiveCategory('All')
    }
  }, [availableCategories, activeCategory, isLoading, cases.length])

  const getCategoryLabel = (category) => {
    if (category === 'All') {
      return t('cases.categories.All')
    }

    // Ищем локализованное имя в categoryOrder по slug
    const found = categoryOrder.find((cat) => cat.slug === category)
    return found ? found.name : category
  }

  const [showHelp, setShowHelp] = useState(false)

  return (
    <div className="text-white pb-6">
      {/* Sticky Balance Header - прилипает при скролле */}
      <div className="z-10 backdrop-blur-sm border-b border-white/10 px-6 py-3 mb-6 lg:border-none">
        <div className="flex items-center justify-between lg:justify-center">
          <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2 lg:text-4xl uppercase italic">
            <Package className="w-6 h-6 lg:w-8 lg:h-8" /> {t('cases.title')}
            <button
              onClick={() => setShowHelp(true)}
              className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
            >
              <HelpCircle className="w-5 h-5 text-text-secondary" />
            </button>
          </h1>
          <button
            onClick={() => navigate('/payment')}
            className="flex items-center gap-2 bg-surface px-4 py-2 rounded-lg border border-accent-primary/30 hover:border-accent-primary transition-all cursor-pointer hover:scale-105 lg:hidden"
          >
            <Gem size={18} className="text-accent-primary" />
            <span className="font-bold text-accent-primary">{balance.toLocaleString()}</span>
          </button>
        </div>
      </div>

      {/* Content Container */}
      <div className="px-6">

      {/* Переключатель игр */}
      {!(isLoading && cases.length === 0) && !error && games.length > 0 && (
        <GameTabs
          games={games}
          activeGame={activeGame}
          onSelect={(slug) => {
            playSound('pick')
            setActiveGame(slug)
          }}
        />
      )}

      {/* Табы категорий — скрываем только при первой загрузке или ошибке */}
      {!(isLoading && cases.length === 0) && !error && availableCategories.length > 0 && (
        <div className="mb-6">
          <div className="flex gap-2 flex-wrap justify-center">
            {visibleCategoryTabs.map((category) => {
              const isActive = activeCategory === category
              const isLimitedTab = category === 'limited'
              const isPrivateTab = category === 'private'
              
              return (
                <button
                  key={category}
                  onClick={() => {
                    playSound('pick')
                    setActiveCategory(category)
                  }}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-accent-primary text-dark-primary'
                      : isLimitedTab
                        ? 'bg-gradient-to-r from-emerald-400/20 via-cyan-400/15 to-violet-500/20 text-emerald-200'
                        : isPrivateTab
                          ? 'bg-gradient-to-r from-amber-400/20 via-orange-400/15 to-rose-500/20 text-amber-200'
                          : 'bg-surface text-text-secondary hover:bg-surface/80'
                  }`}
                >
                  {getCategoryLabel(category)}
                </button>
              )
            })}

            {hasHiddenCategories && (
              <button
                onClick={() => {
                  playSound('pick')
                  setShowAllCategories((previousValue) => !previousValue)
                }}
                className="flex items-center justify-center rounded-lg border border-white/10 bg-surface px-4 py-2 text-text-secondary transition-colors hover:bg-surface/80 hover:text-white"
              >
                {showAllCategories ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Loading State — только при первой загрузке, при обновлении показываем старые данные */}
      {isLoading && cases.length === 0 ? (
        <div className="flex justify-center items-center py-16">
          <Loader className="animate-spin text-accent-primary" size={48} />
        </div>
      ) : error && cases.length === 0 ? (
        /* Error State */
        <div className="text-center py-12">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {t('common.error')}
          </h3>
          <p className="text-text-secondary mb-4">
            {error}
          </p>
          <button 
            onClick={() => loadCases()}
            className="bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary px-6 py-2 rounded-lg font-semibold hover:opacity-90 transition-all"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {visibleSections.map(({ category, cases: categoryCases }) => (
            <section key={category}>
              <h1 className="flex flex-col items-center gap-2.5 mt-12 mb-6 lg:mb-8 text-3xl font-black text-accent-primary lg:text-5xl uppercase italic after:content-[''] after:block after:w-3/5 lg:after:w-1/5 after:h-[2px] after:rounded-[2px] after:bg-[linear-gradient(90deg,transparent,var(--accent-primary,#00FF7F),var(--accent-secondary,#4EFFC6),var(--accent-primary,#00FF7F),transparent)]">
                {getCategoryLabel(category)}
              </h1>

              <div className="flex flex-wrap justify-center gap-3">
                {categoryCases.map((caseItem) => (
                  <div
                    key={caseItem.id}
                    onClick={() => handleCaseClick(caseItem.id)}
                    onMouseEnter={() => {
                      handleCaseHover()
                      setShiningCaseId(caseItem.id)
                    }}
                    onMouseLeave={() => {
                      setShiningCaseId((currentCaseId) => currentCaseId === caseItem.id ? null : currentCaseId)
                    }}
                    className="group w-[calc((100%-0.75rem)/2)] lg:w-[calc((100%-3rem)/5)] bg-surface rounded-lg border border-white border-opacity-10 overflow-hidden cursor-pointer transition-colors duration-300 ease-out hover:border-accent-primary/50"
                  >
                    <div className="relative aspect-square bg-gradient-to-br from-dark-primary to-surface flex items-center justify-center overflow-hidden">
                      {shiningCaseId === caseItem.id && (
                        <motion.div
                          key={caseItem.id}
                          className="pointer-events-none absolute inset-y-[-10%] left-[-45%] z-10 w-[60%] rotate-[18deg] mix-blend-screen"
                          style={{
                            background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.02) 25%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.02) 75%, rgba(255,255,255,0) 100%)',
                            boxShadow: '0 0 16px rgba(255, 255, 255, 0.08)'
                          }}
                          initial={{ x: '-60%', opacity: 1 }}
                          animate={{ x: '320%', opacity: [1, 1, 0] }}
                          transition={{ duration: 0.3, ease: 'linear' }}
                          onAnimationComplete={() => {
                            setShiningCaseId((currentCaseId) => currentCaseId === caseItem.id ? null : currentCaseId)
                          }}
                        />
                      )}
                      <img 
                        src={caseItem.img} 
                        alt={caseItem.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain transition-transform duration-300 ease-out group-hover:scale-110"
                      />
                    </div>

                    <div className="p-3">
                      <h3 className="text-sm font-bold text-white mb-2 leading-tight">
                        {caseItem.name}
                      </h3>

                      {/* Бейджи лимитов и приватности */}
                      {(caseItem.is_private && caseItem.discount > 0) || (caseItem.opens_left != null && caseItem.opens_left > 0) || (caseItem.minutes_left != null && caseItem.minutes_left > 0) ? (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {caseItem.is_private && caseItem.discount > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-primary/20 text-accent-primary border border-accent-primary/30">
                              -{caseItem.discount}%
                            </span>
                          )}
                          {caseItem.opens_left != null && caseItem.opens_left > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-300 border border-red-500/30">
                              <Flame size={10} />
                              {t('cases.opensLeft', { count: caseItem.opens_left })}
                            </span>
                          )}
                          {caseItem.minutes_left != null && caseItem.minutes_left > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500/20 text-orange-300 border border-orange-500/30">
                              <Clock size={10} />
                              {formatMinutesLeft(caseItem.minutes_left, t)}
                            </span>
                          )}
                        </div>
                      ) : null}

                      {/* Цена: для приватных — зачёркнутая + со скидкой */}
                      {caseItem.is_private && caseItem.price_discounted != null ? (
                        <div className="flex items-center gap-2">
                          {caseItem.price_discounted === 0 ? (
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-sm text-accent-primary">
                                {t('cases.free')}
                              </span>
                              {caseItem.price > 0 && (
                                <span className="flex items-center gap-0.5 text-xs text-text-secondary line-through">
                                  {Number(caseItem.price).toLocaleString()}
                                </span>
                              )}
                            </div>
                          ) : (
                            <>
                              <span className="flex items-center gap-1 font-bold text-sm text-accent-primary">
                                <span>{Number(caseItem.price_discounted).toLocaleString()}</span>
                                <Gem size={14} />
                              </span>
                              {caseItem.price > caseItem.price_discounted && (
                                <span className="text-xs text-text-secondary line-through">
                                  {Number(caseItem.price).toLocaleString()}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <div className={`flex items-center gap-1 font-bold text-sm ${
                          user?.balance <= caseItem.price ? 'text-text-secondary' : 'text-accent-primary'
                        }`}>
                          <span>{Number(caseItem.price).toLocaleString()}</span>
                          <Gem size={14} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Empty State - показываем если нет кейсов в выбранной категории */}
      {!isLoading && !error && visibleSections.length === 0 && (
        <div className="text-center py-12">
          <Package size={64} className="mx-auto text-text-secondary mb-4" />
          <h3 className="text-xl font-semibold text-text-secondary mb-2">
            {t('cases.noCases')}
          </h3>
          <p className="text-text-secondary">
            {t('cases.noCasesDescription')}
          </p>
        </div>
      )}

      <TeamCapturePreview mode="case" className="mx-auto mt-8 max-w-3xl" />

      </div>
      {/* End Content Container */}

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={t('cases.helpTitle')}
        steps={[
          t('cases.helpStep1'),
          t('cases.helpStep2'),
          t('cases.helpStep3'),
          t('cases.helpStep4')
        ]}
        buttonText={t('cases.helpGotIt')}
      />
    </div>
  )
}

export default Cases
